import clsx from 'clsx'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled, { css } from 'styled-components'

import { horizontalChildSpacing, verticalChildSpacing } from 'components/utils/spacing'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'

import type { AccountAccessModalResult } from './AccountAccessModal.types'
import SocialLoginButton from './SocialLoginButton/SocialLoginButton'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import Label from 'LuxKit/Label'
import { getLastUsedLoginMethodLabel } from 'lib/auth/lastUsedLoginMethod'
import type { LastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import type { SocialLoginButtonVariant } from './SocialLoginButton/SocialLoginButton.constants'

export const ACCOUNT_ACCESS_LAST_USED_TAG_CLASS = 'account-access-last-used-tag'

const SocialContainer = styled.div`
  margin-bottom: ${rem(48)};

  ${verticalChildSpacing[12]}

  &.inline {
    display: flex;
    width: 100%;

    > * {
      flex: 1;
    }

    ${verticalChildSpacing[0]};
    ${horizontalChildSpacing[12]};
  }

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

const AppLinks = styled(ClickableLink)`
  display: block;
`

const Img = styled(Image)`
  max-width: 100%;
`

const SocialButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`

const lastUsedTagPosition = css`
  position: absolute;
  top: 0;
  transform: translateY(-67%);
  inset-inline-end: 0;
  pointer-events: none;
  z-index: 1;
`

const LastUsedStatusTag = styled(StatusLabel)`
  display: inline-flex;
  align-items: center;
  ${lastUsedTagPosition}
`

const LastUsedLoyaltyTag = styled(Label)`
  display: inline-flex;
  align-items: center;
  ${lastUsedTagPosition}
`

interface Props {
  mode: 'login' | 'join'
  isBlocked?: boolean
  hideText?: boolean
  inline?: boolean
  classNameFacebook?: string
  classNameGoogle?: string
  className?: string
  onPrivateAppleEmailLogin?: (appleLoginData: App.AppleLoginData, email: string) => void
  lastUsedLoginMethod?: LastUsedLoginMethod
  collectPhone?: boolean
  buttonVariant?: SocialLoginButtonVariant
  lastUsedLabelVariant?: 'confirmation' | 'loyalty-infinite-dark'
}

/**
 * Determines if we're currently in the facebook apps internal browser.
 *
 * It can sometimes have considerably different behaviour compared regular web browsers.
 * @returns Whether or not we're in the facebook app browser
 */
function isFacebookApp() {
  if (IS_SSR) {
    return false
  }

  const ua = window.navigator?.userAgent || window.navigator?.vendor || window?.opera
  return (ua.indexOf('FBAN') > -1) || (ua.indexOf('FBAV') > -1)
}

function LastUsedLoginTag({
  method,
  lastUsedLoginMethod,
  lastUsedLabel,
  lastUsedLabelVariant,
}: {
  method: LastUsedLoginMethod
  lastUsedLoginMethod?: LastUsedLoginMethod
  lastUsedLabel: string
  lastUsedLabelVariant: 'confirmation' | 'loyalty-infinite-dark'
}) {
  if (lastUsedLoginMethod !== method) {
    return null
  }

  if (lastUsedLabelVariant === 'loyalty-infinite-dark') {
    return <LastUsedLoyaltyTag
      className={ACCOUNT_ACCESS_LAST_USED_TAG_CLASS}
      kind="primary"
      variant="loyalty-infinite-dark"
    >
      {lastUsedLabel}
    </LastUsedLoyaltyTag>
  }

  return <LastUsedStatusTag
    className={ACCOUNT_ACCESS_LAST_USED_TAG_CLASS}
    variant="confirmation"
  >
    {lastUsedLabel}
  </LastUsedStatusTag>
}

function AccountAccessSocialButtons(props: Props) {
  const {
    isBlocked,
    classNameFacebook,
    classNameGoogle,
    className,
    mode,
    hideText,
    inline,
    onPrivateAppleEmailLogin,
    lastUsedLoginMethod,
    collectPhone,
    buttonVariant,
    lastUsedLabelVariant = 'confirmation',
  } = props

  const intl = useIntl()
  const isFBApp = isFacebookApp()

  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const loggedIn = useAppSelector(selectLoggedIn)

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const { isAnyVariant: isLoginWithFacebookEnabled } = useExperiment(Experiments.croLoginWithFacebook)

  const shouldShowGoogle = Boolean(config.GOOGLE_APP_ID)
  const shouldShowApple = Boolean(config.APPLE_APP_ID)
  const shouldShowFacebook = Boolean(config.FACEBOOK_APP_ID) && isLoginWithFacebookEnabled

  const iphoneAlt = intl.formatMessage({
    id: 'KOjW0JF-t-MaIRZCGXIH',
    defaultMessage: 'Download the iPhone app',
    description: 'Accessibility alt text for App Store download button image',
  })

  const androidAlt = intl.formatMessage({
    id: 'iztpZZCENXscYUUM_zq2',
    defaultMessage: 'Download the Android app',
    description: 'Accessibility alt text for Google Play download button image',
  })

  const lastUsedLabel = getLastUsedLoginMethodLabel(intl)

  return (
    <SocialContainer className={clsx(className, { inline })}>
      {isFBApp && (
        <Group direction="horizontal" horizontalAlign="center" gap={8}>
          <AppLinks href="https://app.luxuryescapes.com/3WHB/1aab092" target="_blank" rel="noopener">
            <Img id="App_Store_suxzv1" alt={iphoneAlt} width={158} />
          </AppLinks>
          <AppLinks href="https://app.luxuryescapes.com/3WHB/feb04c69" target="_blank" rel="noopener">
            <Img id="Google_Play_o1xn2t" alt={androidAlt} width={158} />
          </AppLinks>
        </Group>
      )}
      {!isFBApp && (
        <ErrorBoundary fallback={() => null} reportErrors>
          {shouldShowGoogle && (
            <SocialButtonWrapper>
              <LastUsedLoginTag
                method="google"
                lastUsedLoginMethod={lastUsedLoginMethod}
                lastUsedLabel={lastUsedLabel}
                lastUsedLabelVariant={lastUsedLabelVariant}
              />
              <SocialLoginButton
                mode={mode}
                isBlocked={isBlocked}
                className={classNameGoogle}
                source="google"
                iconOnly={hideText}
                collectPhone={collectPhone}
                variant={buttonVariant}
              />
            </SocialButtonWrapper>
          )}
          {shouldShowApple && (
            <SocialButtonWrapper>
              <LastUsedLoginTag
                method="apple"
                lastUsedLoginMethod={lastUsedLoginMethod}
                lastUsedLabel={lastUsedLabel}
                lastUsedLabelVariant={lastUsedLabelVariant}
              />
              <SocialLoginButton
                mode={mode}
                isBlocked={isBlocked}
                className={classNameGoogle}
                source="apple"
                iconOnly={hideText}
                onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
                variant={buttonVariant}
              />
            </SocialButtonWrapper>
          )}
          {shouldShowFacebook && (
            <SocialButtonWrapper>
              <LastUsedLoginTag
                method="facebook"
                lastUsedLoginMethod={lastUsedLoginMethod}
                lastUsedLabel={lastUsedLabel}
                lastUsedLabelVariant={lastUsedLabelVariant}
              />
              <SocialLoginButton
                mode={mode}
                isBlocked={isBlocked}
                className={classNameFacebook}
                source="facebook"
                iconOnly={hideText}
                variant={buttonVariant}
              />
            </SocialButtonWrapper>
          )}
        </ErrorBoundary>
      )}
    </SocialContainer>
  )
}

export default AccountAccessSocialButtons
